import { Link } from 'react-router-dom'
import { Layers, Zap, Users, Heart, BookOpen, Clock, MapPin, Briefcase } from 'lucide-react'
import Seo from '../components/Seo'

const whyJoin = [
  {
    icon: Heart,
    title: 'Meaningful work',
    description: 'Build technology that helps organizations actually listen to the people they serve.',
  },
  {
    icon: Zap,
    title: 'Ship fast',
    description: "We're early stage, so your work has immediate impact. No bureaucracy.",
  },
  {
    icon: BookOpen,
    title: 'Learn constantly',
    description: "AI is moving fast and we're at the frontier. Stay sharp.",
  },
  {
    icon: Users,
    title: 'Be trusted',
    description: "We hire people who don't need to be managed. Autonomy is the default.",
  },
]

const values = [
  {
    title: 'Depth Over Surface',
    description:
      "We dig deeper. Whether it's a customer conversation or a technical problem, we don't stop at the first answer.",
  },
  {
    title: 'Clarity Over Complexity',
    description:
      'We make complex things simple. Our product should feel obvious. Our communication should be direct.',
  },
  {
    title: 'Speed Over Perfection',
    description:
      'We ship and iterate. Done is better than perfect. Feedback is faster than planning.',
  },
  {
    title: 'Trust Over Process',
    description:
      'We hire capable people and get out of their way. Autonomy is the default.',
  },
]

const benefits = [
  { icon: Briefcase, title: 'Competitive Salary', description: 'Market-rate compensation' },
  { icon: Layers, title: 'Equity', description: 'Meaningful ownership in the company' },
  { icon: MapPin, title: 'Remote-Friendly', description: 'Work from Madison or anywhere' },
  { icon: Clock, title: 'Flexible Schedule', description: 'Results matter, not hours' },
  { icon: Heart, title: 'Health Insurance', description: 'Medical, dental, vision' },
  { icon: BookOpen, title: 'Learning Budget', description: 'Conference attendance, courses, books' },
]

const openPositions = [
  {
    title: 'Business Development — Event Planning & Conferences',
    department: 'Sales',
    type: 'Full-time',
    location: 'Remote / Madison, WI',
    description: 'Own the go-to-market for event intelligence. Help conference organizers, trade show producers, and corporate event teams use PrismScope to capture attendee, speaker, and sponsor feedback.',
    href: '/contact',
  },
  {
    title: 'Business Development — Early-Stage SaaS & Startups',
    department: 'Sales',
    type: 'Full-time',
    location: 'Remote / Madison, WI',
    description: 'Help early-stage B2C SaaS companies find product-market fit faster. You understand the startup ecosystem and can speak the language of founders, product leads, and investors.',
    href: '/contact',
  },
  {
    title: 'Business Development — Banking & Financial Services',
    department: 'Sales',
    type: 'Full-time',
    location: 'Remote / Madison, WI',
    description: 'Bring conversational intelligence to banks, credit unions, and financial services firms. Help them reduce churn, improve member experience, and replace outdated survey programs.',
    href: '/contact',
  },
  {
    title: 'Business Development — HR & Employee Engagement',
    department: 'Sales',
    type: 'Full-time',
    location: 'Remote / Madison, WI',
    description: 'Sell into HR and People Operations teams. Help organizations replace employee surveys with adaptive conversations that surface what people actually think and feel.',
    href: '/contact',
  },
]

export default function Careers() {
  return (
    <div>
      <Seo
        title="Careers"
        description="Join PrismScope — a small team in Madison, Wisconsin changing how organizations understand their customers and employees."
        path="/careers"
      />

      {/* Hero Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-intelligence-900 mb-4">
            Build the Future of Feedback
          </h1>
          <p className="text-xl text-intelligence-600 max-w-3xl mx-auto">
            Join a team that&apos;s changing how organizations understand their customers and employees.
          </p>
        </div>
      </section>

      {/* Why PrismScope */}
      <section className="bg-intelligence-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              Why Work Here
            </h2>
            <p className="text-lg text-intelligence-600">
              We&apos;re a small team tackling a big problem.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyJoin.map((item) => (
              <div key={item.title} className="bg-white rounded-xl border border-intelligence-200 p-6">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-semibold text-intelligence-900 mb-2">{item.title}</h3>
                <p className="text-intelligence-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              What We Believe
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {values.map((value, index) => (
              <div key={value.title} className="bg-intelligence-50 rounded-xl p-6">
                <div className="flex items-center mb-3">
                  <span className="w-8 h-8 bg-accent-100 text-accent-600 rounded-full flex items-center justify-center font-semibold text-sm mr-3">
                    {index + 1}
                  </span>
                  <h3 className="font-semibold text-intelligence-900">{value.title}</h3>
                </div>
                <p className="text-intelligence-600 ml-11">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-intelligence-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">Benefits</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="bg-white rounded-xl border border-intelligence-200 p-6 flex items-start">
                <div className="w-10 h-10 bg-prism-emerald/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <benefit.icon className="w-5 h-5 text-prism-emerald" />
                </div>
                <div>
                  <h3 className="font-semibold text-intelligence-900">{benefit.title}</h3>
                  <p className="text-intelligence-600 text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      {openPositions.length > 0 ? (
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                Open Positions
              </h2>
            </div>
            <div className="space-y-4">
              {openPositions.map((job) => (
                <Link
                  key={job.title}
                  to={job.href}
                  className="block bg-intelligence-50 rounded-xl border border-intelligence-200 p-6 hover:shadow-md hover:border-primary-300 transition-all"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-intelligence-900">{job.title}</h3>
                      <p className="text-intelligence-500">{job.department}</p>
                    </div>
                    <span className="px-3 py-1 bg-prism-emerald/10 text-prism-emerald text-sm rounded-full">
                      {job.type}
                    </span>
                  </div>
                  {job.description && (
                    <p className="text-sm text-intelligence-600 mb-4">{job.description}</p>
                  )}
                  <div className="flex items-center text-sm text-intelligence-500">
                    <MapPin className="w-4 h-4 mr-1" />
                    {job.location}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              No Open Positions
            </h2>
            <p className="text-intelligence-600 mb-8">
              We don&apos;t have any open roles right now, but we&apos;re always interested in meeting
              talented people. Drop us a line if you think you&apos;d be a good fit.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-md transition-colors"
            >
              Get in Touch
            </Link>
          </div>
        </section>
      )}
    </div>
  )
}
